import { FC } from 'react';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import tw from 'twin.macro';
import 'styled-components/macro';

import theme from './theme';

import '../assets/index.css';
import 'tailwindcss/lib/css/preflight.css';

const GlobalStyles = createGlobalStyle`
  // Temp fix because twin.macro does not include keyframes when applying animate-spin util
  // @see https://github.com/ben-rogerson/twin.macro/issues/92#issuecomment-678154951
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: .5;
    }
  }

  @keyframes ping {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    75%, 100% {
      transform: scale(2);
      opacity: 0;
    }
  }

  /*
    Couldn't just use the GlobalStyles export from twin.macro as suggested in their docs
    as it affects other parts of CSS that we don't want to affect
    like setting some additional default styles for the form controls.
    This is required at least for the transforms and ring shadows to work.
    e.g. Modifying this to :root breaks certain classes like scale-75.
    The potential bad thing is that we end up only with a chunk of the default global styles.

    See:
      - https://github.com/ben-rogerson/twin.examples/blob/master/react-styled-components/README.md#add-the-global-styles
      - https://github.com/ben-rogerson/twin.macro/blob/2.8.1/src/config/globalStyles.js
  */
  *,
  ::before,
  ::after {
    --tw-backdrop-blur: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-brightness: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-contrast: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
    --tw-backdrop-grayscale: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-hue-rotate: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-invert: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-opacity: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-saturate: var(--tw-empty,/*!*/ /*!*/);
    --tw-backdrop-sepia: var(--tw-empty,/*!*/ /*!*/);
    --tw-blur: var(--tw-empty,/*!*/ /*!*/);
    --tw-brightness: var(--tw-empty,/*!*/ /*!*/);
    --tw-contrast: var(--tw-empty,/*!*/ /*!*/);
    --tw-drop-shadow: var(--tw-empty,/*!*/ /*!*/);
    --tw-filter: var(--tw-blur) var(--tw-brightness) var(--tw-contrast) var(--tw-grayscale) var(--tw-hue-rotate) var(--tw-invert) var(--tw-saturate) var(--tw-sepia) var(--tw-drop-shadow);
    --tw-grayscale: var(--tw-empty,/*!*/ /*!*/);
    --tw-hue-rotate: var(--tw-empty,/*!*/ /*!*/);
    --tw-invert: var(--tw-empty,/*!*/ /*!*/);
    --tw-ring-color: rgba(59, 130, 246, 0.5);
    --tw-ring-inset: var(--tw-empty,/*!*/ /*!*/);
    --tw-ring-offset-color: #fff;
    --tw-ring-offset-shadow: 0 0 #0000;
    --tw-ring-offset-width: 0px;
    --tw-ring-shadow: 0 0 #0000;
    --tw-rotate: 0;
    --tw-saturate: var(--tw-empty,/*!*/ /*!*/);
    --tw-scale-x: 1;
    --tw-scale-y: 1;
    --tw-sepia: var(--tw-empty,/*!*/ /*!*/);
    --tw-shadow: 0 0 #0000;
    --tw-skew-x: 0;
    --tw-skew-y: 0;
    --tw-transform: translateX(var(--tw-translate-x)) translateY(var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
    --tw-translate-x: 0;
    --tw-translate-y: 0;

    /*
      These styles are not being applied from preflight.css when building for produciton.
      So we're manually adding them here.
    */
    border-width: 0;
    border-style: solid;
  }

  html {
    ${tw`font-sans`}
    ${tw`text-root-base sm:text-root-sm lg:text-root-lg xl:text-root-xl`}
    ${tw`leading-normal`}
    ${tw`text-primary-medium bg-gray-5`}
  }
`;

const ThemeWrapper: FC = ({ children }) => (
  <ThemeProvider theme={theme}>
    <div>
      <GlobalStyles />
      {children}
    </div>
  </ThemeProvider>
);

export default ThemeWrapper;
